import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { ErrorNotice, useAction } from "../components/ui";
import { api, type Candidate, type CandidateHit, type CandidateSummary } from "../lib/api";
import { displaySkill, formatSalary } from "../lib/format";
import { whenLast } from "../lib/dates";
import { CandidateForm } from "./CandidateForm";
import { AddedNotice, AddToPool } from "./AddToPool";

const pad = (n: number) => String(n).padStart(2, "0");

export function candidateLine(c: CandidateSummary): string {
  const role = [c.currentTitle, c.currentEmployer && `at ${c.currentEmployer}`].filter(Boolean).join(" ");
  return [role, c.location, formatSalary(c.salaryExpectation, null, c.currency).replace("from ", "")].filter(Boolean).join(" · ");
}

/** Where they stand: jobs they're on or placed, and when someone last logged contact. */
function progressLine(c: CandidateSummary): string {
  const jobs =
    c.jobs.active > 0
      ? `On ${c.jobs.active} job${c.jobs.active === 1 ? "" : "s"}`
      : c.jobs.placed > 0
        ? "Placed"
        : "Not on a job";
  return `${jobs} · ${c.lastContact ? `last contact ${whenLast(c.lastContact)}` : "no contact logged"}`;
}

export function Candidates() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [location, setLocation] = useState("");
  const [skills, setSkills] = useState<string[]>([]);
  const [status, setStatus] = useState<"active" | "inactive" | "all">("active");
  const [popular, setPopular] = useState<{ skill: string; count: number }[]>([]);
  const [results, setResults] = useState<CandidateHit[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [pooling, setPooling] = useState(false);
  const [pooled, setPooled] = useState<{ pool: { id: string; name: string }; message: string } | null>(null);
  const load = useAction();
  // The full example doesn't fit a phone's search box.
  const [narrow, setNarrow] = useState(() => window.matchMedia("(max-width: 40rem)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 40rem)");
    const onChange = () => setNarrow(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    api
      .candidateSkills()
      .then((r) => setPopular(r.skills.slice(0, 12)))
      .catch(() => {});
  }, []);

  // Debounced search; stale responses are ignored.
  useEffect(() => {
    const query = q.trim();
    if (query.length === 1) return;
    let current = true;
    const timer = setTimeout(() => {
      void load.run(async () => {
        const res = await api.candidates({ q: query || undefined, skills, location: location.trim() || undefined, status });
        if (current) setResults(res.candidates);
      });
    }, 250);
    return () => {
      current = false;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, location, skills, status]);

  const toggleSkill = (s: string) => setSkills((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));

  return (
    <>
      <div className="page-head">
        <p className="label">Candidates</p>
        <h1 className="display">
          The people you <em>place</em>.
        </h1>
        <p className="lede">Search whole profiles and the text of their CVs; narrow by skills and location. Private to your agency.</p>
      </div>

      <section className="section" aria-label="Search candidates">
        <div className="section-head">
          <div className="tabs" role="group" aria-label="Candidate status">
            {(["active", "inactive", "all"] as const).map((s) => (
              <button key={s} type="button" className="tab" aria-pressed={status === s} onClick={() => setStatus(s)}>
                {s === "active" ? "Active" : s === "inactive" ? "Inactive" : "All"}
              </button>
            ))}
          </div>
          <span className="button-row">
            <Link className="link-btn" to="/import?type=candidates">
              Import spreadsheet
            </Link>
            <button type="button" className="link-btn" onClick={() => setAdding((v) => !v)}>
              {adding ? "Cancel" : "+ Add candidate"}
            </button>
          </span>
        </div>

        {adding && <CandidateForm submitLabel="Add candidate" onCancel={() => setAdding(false)} onSaved={(c) => navigate(`/candidates/${c.id}`)} />}

        <div className="search">
          <label className="sr-only" htmlFor="candidate-search">
            Search candidates
          </label>
          <input
            id="candidate-search"
            type="search"
            autoComplete="off"
            placeholder={narrow ? "Search: java AND react -junior" : "Search, e.g. java AND (react OR vue) -junior"}
            aria-describedby="search-syntax"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <p id="search-syntax" className="search-syntax">
            <span>
              <b>AND</b> both
            </span>
            <span>
              <b>OR</b> either
            </span>
            <span>
              <b>NOT</b> or <b>-</b> leave out
            </span>
            <span>
              <b>"…"</b> exact phrase
            </span>
            <span>
              <b>( )</b> group
            </span>
            <span>
              <b>develop*</b> any ending
            </span>
          </p>
        </div>
        <div className="filters">
          <label className="field compact">
            <span className="label">Location</span>
            <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Any" />
          </label>
          {popular.length > 0 && (
            <div className="skill-filter" role="group" aria-label="Filter by skill">
              <span className="label">Skills</span>
              <div className="chips">
                {popular.map((p) => (
                  <button
                    key={p.skill}
                    type="button"
                    className="chip toggle"
                    aria-pressed={skills.includes(p.skill)}
                    onClick={() => toggleSkill(p.skill)}
                  >
                    {displaySkill(p.skill)} <span className="figures">{p.count}</span>
                  </button>
                ))}
                {skills
                  .filter((s) => !popular.some((p) => p.skill === s))
                  .map((s) => (
                    <button key={s} type="button" className="chip toggle" aria-pressed onClick={() => toggleSkill(s)}>
                      {displaySkill(s)}
                    </button>
                  ))}
              </div>
            </div>
          )}
        </div>

        <ErrorNotice error={load.error} />
        <div className="section-head">
          <span className="label figures">{results ? `${pad(results.length)} ${results.length === 100 ? "shown (refine to see more)" : "found"}` : "…"}</span>
          {results && results.length > 0 && !pooling && (
            <button
              type="button"
              className="link-btn"
              onClick={() => {
                setPooled(null);
                setPooling(true);
              }}
            >
              Add {results.length === 1 ? "them" : `these ${results.length}`} to a pool
            </button>
          )}
        </div>
        <AddedNotice added={pooled} onDismiss={() => setPooled(null)} />
        {pooling && results && (
          <AddToPool
            candidateIds={results.map((c) => c.id)}
            onCancel={() => setPooling(false)}
            onAdded={(pool, message) => {
              setPooling(false);
              setPooled({ pool, message });
            }}
          />
        )}
        <ol className="index">
          {results?.map((c, i) => (
            <li key={c.id} className={c.status === "inactive" ? "inactive" : undefined}>
              <span className="num" aria-hidden>
                {pad(i + 1)}
              </span>
              <span className="main">
                <Link className="primary" to={`/candidates/${c.id}`}>
                  {c.fullName}
                </Link>
                <span className="secondary">{candidateLine(c)}</span>
                <span className="secondary">{progressLine(c)}</span>
                {c.snippet && (
                  <span className="snippet">
                    {c.snippet.map((p, j) => (p.hit ? <mark key={j}>{p.text}</mark> : <span key={j}>{p.text}</span>))}
                  </span>
                )}
                {c.skills.length > 0 && (
                  <span className="chips small">
                    {c.skills.slice(0, 6).map((s) => (
                      <span key={s} className={`chip ${skills.includes(s) ? "hit" : ""}`}>
                        {displaySkill(s)}
                      </span>
                    ))}
                    {c.skills.length > 6 && <span className="label">+{c.skills.length - 6}</span>}
                  </span>
                )}
              </span>
              <span className="actions">{c.cv && <span className="label">CV</span>}</span>
            </li>
          ))}
          {results && results.length === 0 && (
            <li>
              <span className="empty">No candidates match. Clear a filter, or add someone new.</span>
            </li>
          )}
        </ol>
      </section>
    </>
  );
}
